import { Link } from 'react-router-dom';
import { type ScopeLens, buildScopePath } from '../lib/hierarchy-route';
import { useActiveScope } from '../hooks/useActiveScope';
import { HierarchyBreadcrumb } from './HierarchyBreadcrumb';

/**
 * Node-scoped page header (FR-2 + FR-3). Lays out, in order, the URL-derived
 * breadcrumb → the page title (the scope node that owns the tabs) → the tab strip
 * (AC-3.4). Overview / Discovery / Delivery render as underline tabs that are local
 * navigation for the node currently in scope — not site-global nav. The active tab is
 * derived from the URL (`useActiveScope`), so exactly one is always lit within a scope,
 * including on leaf pages where the strip persists (the leaf renders in the panel
 * beneath this header). Each tab links to the node's typed scope route, so switching
 * lens from a leaf scopes up to that node's view for the other lens (AC-2.6). The whole
 * header is hidden when there is no node to be views of — the empty landing and the
 * global-bar action routes (AC-2.5).
 */
/** A page-header tab. The lens tabs link to typed scope routes. The scan action
 *  now lives inline at the bottom of the Discovery/Delivery pages — there is no
 *  separate Scan tab. */
const LENS_TABS: { key: ScopeLens; label: string; tooltip: string; phase?: 'discovery' | 'delivery' }[] = [
  { key: 'overview', label: 'Overview', tooltip: 'Where this scope stands' },
  { key: 'discovery', label: 'Discovery', tooltip: 'Analyze specs in Rally features', phase: 'discovery' },
  { key: 'delivery', label: 'Delivery', tooltip: 'Analyze specs in repos', phase: 'delivery' },
];

export function PageHeader() {
  const { node, lens, showTabs, path } = useActiveScope();

  if (!showTabs || !node) return null;

  const activeKey: ScopeLens = lens;
  const tabs = LENS_TABS.map((t) => ({ ...t, to: buildScopePath(node, t.key) }));

  return (
    <div data-testid="page-header">
      <HierarchyBreadcrumb path={path} />

      {/* Page title: the scope node that owns the tabs, with a tierLabel eyebrow. */}
      <div data-testid="page-title" style={{ marginBottom: '16px' }}>
        {node.tierLabel && (
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--genai-sem-text-secondary, #a8b5c7)',
              marginBottom: '2px',
            }}
          >
            {node.tierLabel}
          </div>
        )}
        <h1 style={{ fontSize: 'var(--genai-text-3xl)', fontWeight: 700, margin: 0, color: 'var(--genai-sem-text-primary)', letterSpacing: '-0.01em' }}>
          {node.name}
        </h1>
      </div>

      <nav
        data-testid="page-header-tabs"
        aria-label="Views of this scope"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          marginBottom: '20px',
          borderBottom: '1px solid var(--genai-sem-border, #1e293b)',
        }}
      >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Link
            key={tab.key}
            to={tab.to}
            title={tab.tooltip}
            data-testid={`page-tab-${tab.key}`}
            data-phase={tab.phase}
            data-active={isActive ? 'true' : undefined}
            aria-current={isActive ? 'page' : undefined}
            style={{
              color: isActive
                ? 'var(--genai-sem-nav-text-active, #0c80df)'
                : 'var(--genai-sem-nav-text, #545759)',
              textDecoration: 'none',
              fontWeight: isActive ? 600 : 400,
              fontSize: 'var(--genai-text-base)',
              padding: '11px 20px',
              borderBottom: isActive
                ? '2px solid var(--genai-sem-nav-border-active, #0c80df)'
                : '2px solid transparent',
              marginBottom: '-1px',
              transition: 'all 0.15s ease',
              letterSpacing: '0.01em',
            }}
          >
            {tab.label}
          </Link>
        );
      })}
      </nav>
    </div>
  );
}
